import { ReactElement, useState, useEffect } from "react";
import { ReactComponent as ChatBubbleIcon } from '../../../assets/icons-redesign/chat-bubble.svg';
import { ReactComponent as Star } from "../../../assets/icons-redesign/ai-star.svg";
import { ReactComponent as Chart } from "../../../assets/icons/Chart.svg";
import { useGetAIGeneratedItemQuery } from "../../../redux/admin/reporting/reporting.endpoints";
import { Remarkable } from "remarkable";
import { AIGeneratedItem, AIGeneratedItemType } from '../../../redux/admin/reporting/reporting.types';

const md = new Remarkable();

interface AIGeneratedItemsProps {
  items: AIGeneratedItem[];
  activeTab?: AIGeneratedItemType;
  onTabChange?: (tab: AIGeneratedItemType) => void;
  isLoading?: boolean;
}

const AIGeneratedItems = ({ items, activeTab: controlledTab, onTabChange, isLoading }: AIGeneratedItemsProps): ReactElement => {
  const [internalTab, setInternalTab] = useState<AIGeneratedItemType | null>(null);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState<number>(0);
  const [copied, setCopied] = useState(false);
  const [isContentLoading, setIsContentLoading] = useState(true);

  const availableTypes = Array.from(new Set(items.map(item => item.type)));
  const defaultTab = availableTypes[0] || null;

  // Tab management
  useEffect(() => {
    if (internalTab === null) {
      if (defaultTab) {
        setInternalTab(defaultTab);
      }
    }
  }, [internalTab, availableTypes, defaultTab]);
  useEffect(() => {
    if (controlledTab && availableTypes.includes(controlledTab)) {
      setInternalTab(controlledTab);
    }
  }, [controlledTab, availableTypes]);
  useEffect(() => {
    setSelectedVersionIndex(0);
  }, [internalTab]);
  const handleTabChange = (tab: AIGeneratedItemType) => {
    setInternalTab(tab);
    onTabChange?.(tab);
  };

  const renderHeaderTab = (type: AIGeneratedItemType, label: string, icon?: ReactElement) => {
    const isActive = internalTab === type;
    return (
      <div className={`flex pt-4 pb-2 px-4 relative`}>
        <div
          className={`cursor-pointer flex items-center gap-1.5 text-sm md:text-base font-medium ${isActive ? 'text-blue-600' : 'text-gray-600'}`}
          onClick={() => handleTabChange(type)}
        >
          {icon}
          {label}
        </div>
        {isActive && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
        )}
      </div>
    );
  };

  const getCurrentTypeVersions = () => {
    if (!internalTab) return [];
    return items
      .filter(item => item.type === internalTab)
      .sort((a, b) => b.version - a.version);
  };

  const versions = getCurrentTypeVersions();
  const { data: itemContent, isLoading: isLoadingContent, refetch } = useGetAIGeneratedItemQuery(
    internalTab ? {
      itemId: versions[0]?.id || '',
      version: versions[0]?.version || 0,
    } : { itemId: '', version: 0 },
    { skip: !internalTab }
  );

  // Add polling effect
  useEffect(() => {
    if (!itemContent) return;

    const hasNullItems = itemContent.some(content => content.item === null);
    if (!hasNullItems) {
      setIsContentLoading(false);
      return;
    }

    setIsContentLoading(true);
    // Start polling
    const pollInterval = setInterval(() => {
      refetch();
    }, 5000); // Poll every 5 seconds

    // Clean up interval after 5 minutes (timeout)
    const timeoutId = setTimeout(() => {
      clearInterval(pollInterval);
      setIsContentLoading(false);
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeoutId);
    };
  }, [itemContent, refetch]);

  const getCurrentContent = () => {
    if (!itemContent || itemContent.length === 0) return null;
    const selectedVersion = versions[selectedVersionIndex]?.version;
    return itemContent.find(content => content.version === selectedVersion) || null;
  };

  const handleCopyContent = () => {
    const content = getCurrentContent()?.item || '';
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleVersionChange = (index: number) => {
    setSelectedVersionIndex(index);
  };

  return (
    <div className="flex flex-col w-full">
      <div className="flex w-full gap-2 top-0 z-40 h-14 px-4 mt-2">
        {availableTypes.includes(AIGeneratedItemType.Summary) &&
          renderHeaderTab(AIGeneratedItemType.Summary, 'Summary',
            <Star
              width={20}
              height={20}
              stroke={internalTab === AIGeneratedItemType.Summary ? '#2563EB' : '#6B7280'}
            />)}
        {availableTypes.includes(AIGeneratedItemType.Detailed) &&
          renderHeaderTab(AIGeneratedItemType.Detailed, 'Detailed Report',
            <Chart
              width={20}
              height={20}
              stroke={internalTab === AIGeneratedItemType.Detailed ? '#2563EB' : '#6B7280'}
            />)}
        {availableTypes.includes(AIGeneratedItemType.Email) &&
          renderHeaderTab(AIGeneratedItemType.Email, 'Email',
            <ChatBubbleIcon
              width={20}
              height={20}
              stroke={internalTab === AIGeneratedItemType.Email ? '#2563EB' : '#6B7280'}
            />)}
      </div>
      <div className="px-8 py-4">
        {internalTab && (
          <div>
            {isLoading || isContentLoading ? (
              <div className="animate-pulse mt-2">
                Generating response...
              </div>
            ) : (
              <>
                <div
                  className="summary lg:max-h-[560px] overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: md.render(getCurrentContent()?.item || 'No content available.') }}
                />
                <div className="flex justify-between items-center mt-6 bg-gray-200 rounded-xl px-4 py-2">
                  <div className="flex items-center gap-2">
                    <button
                      className="p-1 text-gray-700 disabled:opacity-50"
                      disabled={selectedVersionIndex === versions.length - 1}
                      onClick={() => handleVersionChange(selectedVersionIndex + 1)}
                    >
                      ←
                    </button>
                    <span className="text-sm text-gray-700">
                      {'Version '}
                      {versions.length - selectedVersionIndex}
                      {' of '}
                      {versions.length}
                    </span>
                    <button
                      className="p-1 text-gray-700 disabled:opacity-50"
                      disabled={selectedVersionIndex === 0}
                      onClick={() => handleVersionChange(selectedVersionIndex - 1)}
                    >
                      →
                    </button>
                  </div>
                  <button
                    className="px-3 py-1 text-sm text-gray-700 bg-white hover:bg-gray-50 rounded-lg"
                    onClick={handleCopyContent}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIGeneratedItems;